// src/components/ContactModal.js
import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Modal, Paper, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import klasharalogo from '../images/Group 106.png'

const ContactModal = ({
  open,
  onClose,
  contactData,
  onChange,
  contactErrors,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await onSubmit();
    setIsSubmitting(false);
  };

  return (
    <Modal 
      open={open} 
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: isMobile ? 2 : 4,
      }}
    >
      <Paper
        elevation={24}
        sx={{
          maxWidth: '95%',
          width: isMobile ? '100%' : '400px',
          borderRadius: isMobile ? '12px' : '16px',
          background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)',
          position: 'relative',
          overflow: 'hidden',
          maxHeight: isMobile ? '90vh' : '80vh',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
            '&:hover': {
              background: '#555',
            },
          },
        }}
      >
        {/* Decorative top banner */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            height: '8px',
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            zIndex: 1,
          }}
        />

        {/* Main content */}
        <Box sx={{ p: isMobile ? 2.5 : 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: isMobile ? 3 : 4 }}>
            <Box 
              sx={{ 
                width: '80px',
                height: '80px',
                margin: '0 auto',
                mb: 2,
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                  '100%': { transform: 'scale(1)' },
                },
              }}
            >
              <img 
                src={klasharalogo} // Add your logo path here
                alt="Logo"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
            <Typography 
              variant={isMobile ? "h6" : "h5"}
              component="h2" 
              sx={{ 
                fontWeight: 600,
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                mb: 2,
                animation: 'slideDown 0.5s ease-out',
                '@keyframes slideDown': {
                  from: { transform: 'translateY(-20px)', opacity: 0 },
                  to: { transform: 'translateY(0)', opacity: 1 },
                },
              }}
            >
              🎉 Almost There!
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                mb: 1,
                fontSize: isMobile ? '0.9rem' : '1rem',
                animation: 'fadeIn 0.5s ease-out',
                '@keyframes fadeIn': {
                  from: { opacity: 0 },
                  to: { opacity: 1 },
                },
              }}
            >
              Enter your details to receive your personalized visa journey plan
            </Typography>
          </Box>

          {/* Form Fields */}
          <Box 
            sx={{ 
              mb: 3,
              animation: 'slideUp 0.5s ease-out',
              '@keyframes slideUp': {
                from: { transform: 'translateY(20px)', opacity: 0 },
                to: { transform: 'translateY(0)', opacity: 1 },
              },
            }}
          >
            <Box sx={{ position: 'relative', mb: 2.5 }}>
              <PersonIcon 
                sx={{ 
                  position: 'absolute',
                  left: 8,
                  top: 12,
                  color: contactErrors.name ? 'error.main' : 'primary.main',
                  fontSize: isMobile ? '1.2rem' : '1.5rem',
                }} 
              />
              <TextField
                label="Your Name"
                fullWidth
                value={contactData.name}
                onChange={(e) => onChange('name', e.target.value)}
                error={!!contactErrors.name}
                helperText={contactErrors.name}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    pl: 5,
                    height: isMobile ? '48px' : '56px',
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: isMobile ? '0.9rem' : '1rem',
                  },
                }}
              />
            </Box>
            <Box sx={{ position: 'relative', mb: 2.5 }}>
              <PhoneIcon 
                sx={{ 
                  position: 'absolute',
                  left: 8,
                  top: 12,
                  color: contactErrors.mobile ? 'error.main' : 'primary.main',
                  fontSize: isMobile ? '1.2rem' : '1.5rem',
                }} 
              />
              <TextField
                label="Mobile Number"
                fullWidth
                value={contactData.mobile}
                onChange={(e) => onChange('mobile', e.target.value)}
                error={!!contactErrors.mobile}
                helperText={contactErrors.mobile}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    pl: 5,
                    height: isMobile ? '48px' : '56px',
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: isMobile ? '0.9rem' : '1rem',
                  },
                }}
              />
            </Box>
          </Box>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            variant="contained"
            fullWidth
            disabled={!contactData.name || !contactData.mobile || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <WhatsAppIcon />}
            sx={{
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              borderRadius: '8px',
              py: isMobile ? 1.2 : 1.5,
              textTransform: 'none',
              fontSize: isMobile ? '1rem' : '1.1rem',
              fontWeight: 500,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
              },
              '&:disabled': {
                background: '#e0e0e0',
              },
              animation: 'bounce 1s infinite',
              '@keyframes bounce': {
                '0%, 100%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(-3px)' },
              },
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Get Your Free Plan'}
          </Button>

          {/* Trust indicators */}
          <Box 
            sx={{ 
              mt: 3, 
              textAlign: 'center',
              animation: 'fadeIn 0.5s ease-out',
            }}
          >
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                fontSize: isMobile ? '0.7rem' : '0.75rem',
              }}
            >
              🔒 Your information is secure and will not be shared
            </Typography>
          </Box>
        </Box>

        {/* Decorative bottom banner */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            height: '8px',
            background: 'linear-gradient(45deg, #21CBF3 30%, #2196F3 90%)',
            zIndex: 1,
          }}
        />
      </Paper>
    </Modal>
  );
};

export default ContactModal;
